import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/select';
import { Textarea } from '@art-of-hospitality-solutions-llc/ui-kit/components/textarea';
import {
  AppWindow,
  ClipboardList,
  ExternalLink,
  PanelBottom,
  type LucideIcon,
} from 'lucide-react';

import type { ActionValue, ButtonValue, ScreenOption } from '../types';
import FieldShell from './FieldShell';
import RepeaterField from './RepeaterField';
import SegmentedField from './SegmentedField';
import TextInputField from './TextInputField';

type ActionKind = ActionValue['kind'];

const KIND_OPTIONS: { value: ActionKind; label: string; icon: LucideIcon }[] = [
  { value: 'screen', label: 'App Screen', icon: AppWindow },
  { value: 'external', label: 'External URL', icon: ExternalLink },
  { value: 'drawer', label: 'Drawer', icon: PanelBottom },
  { value: 'typeform', label: 'Typeform', icon: ClipboardList },
];

/** A fresh value for a newly picked kind. */
const emptyAction = (kind: ActionKind): ActionValue => {
  switch (kind) {
    case 'screen':
      return { kind, ref: '' };
    case 'external':
      return { kind, url: '' };
    case 'drawer':
      return { kind };
    case 'typeform':
      return { kind, formId: '' };
  }
};

interface ActionPickerFieldProps {
  title?: string;
  description?: string;
  value?: ActionValue;
  /** Screens an internal link may target. */
  screenOptions: ScreenOption[];
  /** Kinds hidden from this picker (e.g. no drawer inside a drawer). */
  excludeKinds?: ActionKind[];
  onChange: (value: ActionValue) => void;
}

/**
 * Sub-form producing the discriminated-union action value: choose the kind,
 * then fill its fields. Kit-executed kinds (screen/external/drawer) and
 * host-delegated kinds (typeform) share the same shape — see the kit's
 * ActionValue docs.
 */
export default function ActionPickerField({
  title,
  description,
  value,
  screenOptions,
  excludeKinds = [],
  onChange,
}: ActionPickerFieldProps) {
  const kinds = KIND_OPTIONS.filter((o) => !excludeKinds.includes(o.value));
  const kind: ActionKind = value?.kind ?? kinds[0]?.value ?? 'screen';

  return (
    <FieldShell title={title} description={description}>
      {/* No box of its own: the field label already groups the kind picker
          with its value control, and the surrounding repeater item (or
          panel) supplies the enclosing surface. */}
      <div className="space-y-2">
        {/* A dropdown, not a segmented control: kinds will keep growing and
            four labels already crowd the panel's width. */}
        <Select
          value={kind}
          onValueChange={(next) => onChange(emptyAction(next as ActionKind))}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {kinds.map(({ value: kindValue, label, icon: Icon }) => (
              <SelectItem key={kindValue} value={kindValue}>
                <span className="flex items-center gap-2">
                  <Icon size={14} className="text-zinc-500" />
                  {label}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {kind === 'screen' && (
          <Select
            value={
              value?.kind === 'screen' && value.ref ? value.ref : undefined
            }
            onValueChange={(ref) => onChange({ kind: 'screen', ref })}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a screen..." />
            </SelectTrigger>
            <SelectContent>
              {screenOptions.map((screen) => (
                <SelectItem key={screen.ref} value={screen.ref}>
                  {screen.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {kind === 'external' && (
          <Input
            type="url"
            placeholder="https://..."
            value={value?.kind === 'external' ? value.url : ''}
            onChange={(e) =>
              onChange({ kind: 'external', url: e.target.value })
            }
          />
        )}

        {kind === 'typeform' && (
          <Input
            placeholder="Typeform form ID"
            value={value?.kind === 'typeform' ? value.formId : ''}
            onChange={(e) =>
              onChange({ kind: 'typeform', formId: e.target.value })
            }
          />
        )}

        {kind === 'drawer' && value?.kind === 'drawer' && (
          <div className="space-y-3">
            <Textarea
              placeholder="Drawer text (the header reuses the card's image and title)..."
              rows={3}
              value={value.body ?? ''}
              onChange={(e) => onChange({ ...value, body: e.target.value })}
            />
            <RepeaterField
              title="Drawer buttons"
              itemLabel="Button"
              maxItems={2}
              tone="contrast"
              getItemTitle={(item) => (item as ButtonValue).label}
              items={(value.buttons ?? []) as Record<string, unknown>[]}
              createItem={() => ({ style: 'primary' })}
              renderItem={(item, onItemChange) => {
                const button = item as ButtonValue;
                return (
                  <>
                    <TextInputField
                      title="Label"
                      value={button.label}
                      onChange={(label) => onItemChange({ ...item, label })}
                    />
                    <ActionPickerField
                      title="Action"
                      value={button.action}
                      screenOptions={screenOptions}
                      // No drawer-in-drawer.
                      excludeKinds={['drawer']}
                      onChange={(action) => onItemChange({ ...item, action })}
                    />
                    <SegmentedField
                      title="Style"
                      options={[
                        { value: 'primary', label: 'Primary' },
                        { value: 'secondary', label: 'Secondary' },
                      ]}
                      value={button.style ?? 'primary'}
                      onChange={(style) => onItemChange({ ...item, style })}
                    />
                  </>
                );
              }}
              onChange={(items) =>
                onChange({ ...value, buttons: items as ButtonValue[] })
              }
            />
          </div>
        )}
      </div>
    </FieldShell>
  );
}
